/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Removed local error prop, relying on global error handling.
 *                                   - Integrated Spinner for submission and lookup loading states.
 *                                   - Ensured `cost` is parsed as float.
 *                                   - Changed default `currency_id` to `null` for new processes.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for process definitions.
 *                      It does not process sensitive data directly.
 * INTENT: Provide a reusable form for creating and editing Process records,
 *         ensuring data integrity and a clear user experience, including currency selection,
 *         now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure input validation is robust and
 *                        that the form state correctly reflects submission and lookup status.
 * LLM_CONFIDENCE: 100% - Standard form component with state management and input/select handling.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Process, ProcessCreate, ProcessUpdate, CurrencyLookupItem } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Spinner } from './ui/spinner.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface ProcessFormProps {
  initialData?: Process | null;
  onSubmit: (data: ProcessCreate | ProcessUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  currencies: CurrencyLookupItem[];
  lookupLoading: boolean;
  entityId: string; // NEW: Add entityId prop
}

const defaultFormData: ProcessCreate = {
  description: '',
  currency_id: null as any,
  cost: 0.0,
  created_by: 'tester',
};

const ProcessForm: React.FC<ProcessFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  currencies,
  lookupLoading,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const [formData, setFormData] = useState<ProcessCreate | ProcessUpdate>(
    initialData
      ? {
          description: initialData.description,
          currency_id: initialData.currency_id || null,
          cost: initialData.cost,
          updated_by: initialData.updated_by || 'tester',
        }
      : defaultFormData
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        description: initialData.description,
        currency_id: initialData.currency_id || null,
        cost: initialData.cost,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  }, []);

  const handleSelectChange = useCallback((id: keyof (ProcessCreate | ProcessUpdate), value: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value === '' || value === 'nil' ? null : parseInt(value, 10),
    }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isFieldVisible('description') && (
        <div>
          <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('description', 'Description')}
          </Label>
          <Input
            id="description"
            type="text"
            required
            value={formData.description || ''}
            onChange={handleChange}
            disabled={isDisabled}
          />
        </div>
      )}
      {isFieldVisible('cost') && (
        <div>
          <Label htmlFor="cost" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('cost', 'Cost')}
          </Label>
          <Input
            id="cost"
            type="number"
            step="0.01"
            required
            value={formData.cost || ''}
            onChange={handleChange}
            disabled={isDisabled}
          />
        </div>
      )}
      {isFieldVisible('currency_id') && (
        <div>
          <Label htmlFor="currency_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('currency_id', 'Currency')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('currency_id', value)}
            value={formData.currency_id ? String(formData.currency_id) : ''}
            disabled={isDisabled}
            required
          >
            <SelectTrigger id="currency_id">
              <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nil" disabled>
                {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'}
              </SelectItem>
              {currencies.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isDisabled || formData.currency_id === null}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ProcessForm;